import { uniq } from 'lodash-es'

import { useUser } from '@/client/context/user'
import { getTutorUrl } from '@/client/services/url'
import { getUserLevelInWorld } from '@/client/services/worlds'

import type { PostDetails, PostEventDetails } from '@/components/Community/Post/types'
import ShwLink from '@/components/ShwLink'
import Alert from '@/components/UI/Alert'

const HELP_CHANNEL_TUTOR_GUIDELINES_URL =
  'https://coda.io/d/How-to-Answer-Help-Channel-Questions_dOuig9DPB1V/How-to-Answer-Help-Channel-Requests_suEiW'

const TOO_MANY_COMMENTERS_MODAL_THRESHOLD = 2

export default function LiveHelpReplyGuidelines({
  worldSlug,
  post,
  postEvents,
}: {
  worldSlug: string
  post: PostDetails
  postEvents: PostEventDetails[]
}): JSX.Element | null {
  const currentUser = useUser()
  const level = getUserLevelInWorld({ user: currentUser, worldSlug })

  if (level == null) {
    return null
  }

  const countUniquePostEventUsers = uniq(
    postEvents.filter(e => e.user.id !== post.user.id).map(e => e.user.id)
  ).length

  const showTooManyCommentsAlert =
    !postEvents.some(e => e.user.id === currentUser.id) &&
    countUniquePostEventUsers >= TOO_MANY_COMMENTERS_MODAL_THRESHOLD &&
    post.channel?.type === 'question' &&
    post.user.id !== currentUser.id

  if (showTooManyCommentsAlert) {
    return (
      <Alert variant="warning">
        {countUniquePostEventUsers} people have already replied to this question. Please be mindful
        of overwhelming this learner with too many responses.
      </Alert>
    )
  }

  if (level === 'member') {
    return (
      <Alert variant="info">
        Remember to guide learners towards the solution, instead of giving away answers. If
        you&apos;d like to help over Zoom, <ShwLink href={getTutorUrl()}>become a tutor</ShwLink>!
      </Alert>
    )
  }

  return (
    <Alert variant="info">
      Remember to guide learners towards the solution, instead of giving away answers. See{' '}
      <ShwLink href={HELP_CHANNEL_TUTOR_GUIDELINES_URL}>here</ShwLink> for tips on responding to
      questions.
    </Alert>
  )
}
